import {
  SpecialistbevisAnsokanFormFieldName,
  SpecialistbevisAnsokanField,
  SpecialistbevisYrke,
  MultiSelectItem,
  LEGITIMATION_MAX_ENTRIES,
  getLegitimationCountryFieldName,
  getLegitimationDateFieldName,
  createEmptyLegitimationEntry,
  EARLIER_SPECIALIST_MAX_ENTRIES,
  getEarlierSpecialistSpecialityFieldName,
  getEarlierSpecialistCountryFieldName,
  getEarlierSpecialistDateFieldName,
  SpecialistbevisTyp,
} from './SpecialistbevisAnsokanForm.types';
import {
  SpecialistbevisAnsokanFormStepOneModel,
  SpecialistbevisAnsokan,
  AnsokanFormTexts,
  LegitimationsDetalj,
  SpecialistbevisDetalj,
} from '../../../models/specialistbevisansokan.model';
// import { FormFieldFile } from 'sos-common/js/commonTypes';
import { FileExtendedDetails } from 'sos-common/js/commonTypes/fileupload.types';
import { ValidationObject } from 'sos-common/js/commonTypes/validation.types';
// import { SelectBoxOption } from 'sos-common/js';
import { LEG_FILE_MIMETYPES, SPEC_FILE_MIMETYPES } from '../../FilKomplettering/FilKomplettering.mimetypes';
import { ComboboxItem } from 'sos-common/js/commonTypes/multiSelect.types';
import { FormFieldFile } from 'sos-common/js/commonTypes/formField.types';
import { SelectBoxOption } from 'sos-common/js/components/FormControls/SelectBox';
import { isDateValid } from 'sos-common/js/utils/date';
import { EducationLocation } from '../../../models/ansokan.model';

// export const EMAIL_NOT_MATCHING = 'E-postadressen stämmer inte överens.';
// export const MANDATORY = 'Den här uppgiften måste fyllas i för att du ska kunna gå vidare.';

export class SpecialistbevisFieldMap {
  private _map: Map<SpecialistbevisAnsokanFormFieldName | string, BaseField>;
  public allFieldsAreValid: boolean = false;
  private _emailNoMatch: string;

  constructor(
    model: SpecialistbevisAnsokanFormStepOneModel,
    ansokan: SpecialistbevisAnsokan,
    formTexts: AnsokanFormTexts,
    educationLocation: EducationLocation,
    specialistBevisTyp: SpecialistbevisTyp,
    translations: (key: string, defaultValue?: string) => string,
    onAdd: (field: FormFieldFile) => void,
    onRemove: (file: FileExtendedDetails, fieldName: string, field: FormFieldFile) => void,
    addMultiSelectItem: (selectedItems: MultiSelectItem[]) => void,
    outsideEuEes: boolean,
    specialistbevisOtherTypeId: string,
    legitimationEntries: LegitimationsDetalj[],
    earlierSpecialistEntries: SpecialistbevisDetalj[],
  ) {
    const map = new Map();
    this._map = map;
    this._emailNoMatch = translations('errorMessages/emailNoMatch');
    const isEducatedInSweden = educationLocation === EducationLocation.EducatedInSweden;
    const isDentist = model?.OccupationId === SpecialistbevisYrke.Tandlakare;
    const isDoctor = model?.OccupationId === SpecialistbevisYrke.Lakare;
    const shouldCollectGeneralDentalPractice = isEducatedInSweden && isDentist;
    const isHslfFs2021_8 = specialistBevisTyp === SpecialistbevisTyp.LAKARE_HSLF_2021_8;
    const shouldCollectBastjanstgoringCertificate = isEducatedInSweden && isDoctor && isHslfFs2021_8;
    const isBastjanstgoring = specialistBevisTyp === SpecialistbevisTyp.INTYG_BASTJANSTGORING;
    const isSjukhusfysiker = model?.OccupationId === SpecialistbevisYrke.Sjukhusfysiker;
    map.set(
      SpecialistbevisAnsokanFormFieldName.Email,
      new EpostField(SpecialistbevisAnsokanFormFieldName.Email, ansokan?.Epost, true, translations),
    );

    map.set(
      SpecialistbevisAnsokanFormFieldName.ConfirmEmail,
      new EpostField(SpecialistbevisAnsokanFormFieldName.ConfirmEmail, ansokan?.Epost, false, translations),
    );

    map.set(
      SpecialistbevisAnsokanFormFieldName.SpecialistbevisType,
      new SpecialistbevisTyper(
        SpecialistbevisAnsokanFormFieldName.SpecialistbevisType,
        model?.SpecialistbevisTyper,
        ansokan.SpecialistbevisUtbildning?.TypAvSpecialistKompetensId,
        !isBastjanstgoring,
        translations,
        !isBastjanstgoring,
      ),
    );

    map.set(
      SpecialistbevisAnsokanFormFieldName.OvrigSpecialistbevisTyp,
      new OvrigtSpecialistbevisTyp(
        SpecialistbevisAnsokanFormFieldName.OvrigSpecialistbevisTyp,
        ansokan.SpecialistbevisUtbildning?.OvrigSpecialistKompetens,
        true,
        translations,
        ansokan.SpecialistbevisUtbildning?.TypAvSpecialistKompetensId === specialistbevisOtherTypeId,
      ),
    );

    if (!outsideEuEes) {
      map.set(
        SpecialistbevisAnsokanFormFieldName.UtbildningsLand,
        new UtbildningsLand(
          SpecialistbevisAnsokanFormFieldName.UtbildningsLand,
          model?.UtbildningsLander,
          ansokan.SpecialistbevisUtbildning?.UtbildningslandId,
          true,
          translations,
        ),
      );
    }

    const shouldCollectOtherCountryInfo = educationLocation !== EducationLocation.EducatedInSweden;
    const hasCertField = new HasCertInOtherCountry(
      SpecialistbevisAnsokanFormFieldName.HasCertInOtherCountry,
      translations,
      ansokan.SpecialistbevisUtbildning?.SpecialistbevisFranAnnatLand,
      true,
      SpecialistbevisAnsokanFormFieldName.HasCertInOtherCountry.toString(),
    );
    hasCertField.ShowField = shouldCollectOtherCountryInfo;
    map.set(SpecialistbevisAnsokanFormFieldName.HasCertInOtherCountry, hasCertField);

    const otherCertCountriesField = new OtherCertCountry(
      SpecialistbevisAnsokanFormFieldName.OtherCertCountries,
      model?.UtbildningsLander,
      ansokan.SpecialistbevisUtbildning?.LandIdForSpecialistBevis ?? [''],
      true,
      translations,
      ansokan.SpecialistbevisUtbildning?.SpecialistbevisFranAnnatLand,
      addMultiSelectItem,
    );
    otherCertCountriesField.ShowField = shouldCollectOtherCountryInfo;
    map.set(SpecialistbevisAnsokanFormFieldName.OtherCertCountries, otherCertCountriesField);

    map.set(
      SpecialistbevisAnsokanFormFieldName.CheckboxValue,
      new CheckboxField(SpecialistbevisAnsokanFormFieldName.CheckboxValue, translations, ansokan?.CheckboxValue),
    );

    if (model.FileUploadText1 !== null) {
      const fileupload1FieldState = new FilesField(
        SpecialistbevisAnsokanFormFieldName.FileUpload1,
        model.FileUploadText1?.Label,
        ansokan?.Fileupload1Files,
        onAdd,
        onRemove,
        translations,
        model.FileUploadText1.Required,
        model.FileUploadText1.Show,
        isEducatedInSweden,
      );
      map.set(SpecialistbevisAnsokanFormFieldName.FileUpload1, fileupload1FieldState);
    }

    if (model.FileUploadText2 !== null) {
      const fileupload2FieldState = new FilesField(
        SpecialistbevisAnsokanFormFieldName.FileUpload2,
        model.FileUploadText2?.Label,
        ansokan?.Fileupload2Files,
        onAdd,
        onRemove,
        translations,
        model.FileUploadText2.Required,
        model.FileUploadText2.Show,
        isEducatedInSweden,
      );
      map.set(SpecialistbevisAnsokanFormFieldName.FileUpload2, fileupload2FieldState);
    }

    if (model.FileUploadText3 !== null) {
      const fileupload3FieldState = new FilesField(
        SpecialistbevisAnsokanFormFieldName.FileUpload3,
        model.FileUploadText3?.Label,
        ansokan?.Fileupload3Files,
        onAdd,
        onRemove,
        translations,
        model.FileUploadText3.Required,
        model.FileUploadText3.Show,
        isEducatedInSweden,
      );
      map.set(SpecialistbevisAnsokanFormFieldName.FileUpload3, fileupload3FieldState);
    }

    if (model.FileUploadText4 !== null) {
      const fileupload4FieldState = new FilesField(
        SpecialistbevisAnsokanFormFieldName.FileUpload4,
        model.FileUploadText4?.Label,
        ansokan?.Fileupload4Files,
        onAdd,
        onRemove,
        translations,
        model.FileUploadText4.Required,
        model.FileUploadText4.Show,
        isEducatedInSweden,
      );
      map.set(SpecialistbevisAnsokanFormFieldName.FileUpload4, fileupload4FieldState);
    }

    if (model.FileUploadText5 !== null) {
      const fileupload5FieldState = new FilesField(
        SpecialistbevisAnsokanFormFieldName.FileUpload5,
        model.FileUploadText5?.Label,
        ansokan?.Fileupload5Files,
        onAdd,
        onRemove,
        translations,
        model.FileUploadText5.Required,
        model.FileUploadText5.Show,
        isEducatedInSweden,
      );
      map.set(SpecialistbevisAnsokanFormFieldName.FileUpload5, fileupload5FieldState);
    }
    if (model.FileUploadText6 !== null) {
      const fileupload6FieldState = new FilesField(
        SpecialistbevisAnsokanFormFieldName.FileUpload6,
        model.FileUploadText6?.Label,
        ansokan?.Fileupload6Files,
        onAdd,
        onRemove,
        translations,
        model.FileUploadText6.Required,
        model.FileUploadText6.Show,
        isEducatedInSweden,
      );
      map.set(SpecialistbevisAnsokanFormFieldName.FileUpload6, fileupload6FieldState);
    }
    if (model.FileUploadText7 !== null) {
      const fileupload7FieldState = new FilesField(
        SpecialistbevisAnsokanFormFieldName.FileUpload7,
        model.FileUploadText7?.Label,
        ansokan?.Fileupload7Files,
        onAdd,
        onRemove,
        translations,
        model.FileUploadText7.Required,
        model.FileUploadText7.Show,
        isEducatedInSweden,
      );
      map.set(SpecialistbevisAnsokanFormFieldName.FileUpload7, fileupload7FieldState);
    }
    if (model.FileUploadText8 !== null) {
      const fileupload8FieldState = new FilesField(
        SpecialistbevisAnsokanFormFieldName.FileUpload8,
        model.FileUploadText8?.Label,
        ansokan?.Fileupload8Files,
        onAdd,
        onRemove,
        translations,
        model.FileUploadText8.Required,
        model.FileUploadText8.Show,
        isEducatedInSweden,
      );
      map.set(SpecialistbevisAnsokanFormFieldName.FileUpload8, fileupload8FieldState);
    }
    if (model.FileUploadText9 !== null) {
      const fileupload9FieldState = new FilesField(
        SpecialistbevisAnsokanFormFieldName.FileUpload9,
        model.FileUploadText9?.Label,
        ansokan?.Fileupload9Files,
        onAdd,
        onRemove,
        translations,
        model.FileUploadText9.Required,
        model.FileUploadText9.Show,
        isEducatedInSweden,
      );
      map.set(SpecialistbevisAnsokanFormFieldName.FileUpload9, fileupload9FieldState);
    }
    if (model.FileUploadText10 !== null) {
      const fileupload10FieldState = new FilesField(
        SpecialistbevisAnsokanFormFieldName.FileUpload10,
        model.FileUploadText10?.Label,
        ansokan?.Fileupload10Files,
        onAdd,
        onRemove,
        translations,
        model.FileUploadText10.Required,
        model.FileUploadText10.Show,
        isEducatedInSweden,
      );
      map.set(SpecialistbevisAnsokanFormFieldName.FileUpload10, fileupload10FieldState);
    }
    if (model.FileUploadText11 !== null) {
      const fileupload11FieldState = new FilesField(
        SpecialistbevisAnsokanFormFieldName.FileUpload11,
        model.FileUploadText11?.Label,
        ansokan?.Fileupload11Files,
        onAdd,
        onRemove,
        translations,
        model.FileUploadText11.Required,
        model.FileUploadText11.Show,
        isEducatedInSweden,
      );
      map.set(SpecialistbevisAnsokanFormFieldName.FileUpload11, fileupload11FieldState);
    }
    if (model.FileUploadText12 !== null) {
      const fileupload12FieldState = new FilesField(
        SpecialistbevisAnsokanFormFieldName.FileUpload12,
        model.FileUploadText12?.Label,
        ansokan?.Fileupload12Files,
        onAdd,
        onRemove,
        translations,
        model.FileUploadText12.Required,
        model.FileUploadText12.Show,
        isEducatedInSweden,
      );
      map.set(SpecialistbevisAnsokanFormFieldName.FileUpload12, fileupload12FieldState);
    }
    map.set(
      SpecialistbevisAnsokanFormFieldName.TermsAndConditions,
      new TermsAndConditionsField(
        SpecialistbevisAnsokanFormFieldName.TermsAndConditions,
        formTexts.SamtyckeLabel,
        translations,
      ),
    );
    if (isEducatedInSweden) {
      map.set(
        SpecialistbevisAnsokanFormFieldName.ExaminationDate,
        new ExaminationDateField(
          SpecialistbevisAnsokanFormFieldName.ExaminationDate,
          ansokan?.SpecialistbevisUtbildning?.ExamensDatum,
          translations,
          'examinationDate',
          !isSjukhusfysiker,
        ),
      );
    }

    if (shouldCollectBastjanstgoringCertificate) {
      map.set(
        SpecialistbevisAnsokanFormFieldName.GeneralServiceCertificateDate,
        new ExaminationDateField(
          SpecialistbevisAnsokanFormFieldName.GeneralServiceCertificateDate,
          ansokan?.SpecialistbevisUtbildning?.BasTjanstgoringsDatum ??
            ansokan?.SpecialistbevisUtbildning?.BastjanstgoringsIntygDatum,
          translations,
          'specialistbevisAnsokan/bastjanstgoringsCertificateDate',
        ),
      );
    }

    if (isEducatedInSweden) {
      map.set(
        SpecialistbevisAnsokanFormFieldName.WorkPlace,
        new WorkPlace(
          SpecialistbevisAnsokanFormFieldName.WorkPlace,
          ansokan?.SpecialistbevisUtbildning?.Arbetsplats ?? ansokan?.Arbetsplats,
          true,
          translations,
        ),
      );

      if (!isBastjanstgoring) {
        this._initializeLegitimationFields(model?.UtbildningsLander ?? [], translations, legitimationEntries);
        this._initializeEarlierSpecialistFields(
          model?.SpecialistbevisTyper ?? [],
          model?.UtbildningsLander ?? [],
          translations,
          earlierSpecialistEntries,
        );
      }
    }

    if (shouldCollectGeneralDentalPractice) {
      map.set(
        SpecialistbevisAnsokanFormFieldName.GeneralDentalPracticeCountry,
        new GeneralDentalPracticeCountryField(
          SpecialistbevisAnsokanFormFieldName.GeneralDentalPracticeCountry,
          model?.UtbildningsLander ?? [],
          ansokan?.SpecialistbevisUtbildning?.TandlakarPraktikDetalj?.LandId,
          translations,
        ),
      );

      map.set(
        SpecialistbevisAnsokanFormFieldName.GeneralDentalPracticeDate,
        new ExaminationDateField(
          SpecialistbevisAnsokanFormFieldName.GeneralDentalPracticeDate,
          ansokan?.SpecialistbevisUtbildning?.TandlakarPraktikDetalj?.Datum,
          translations,
          'generalDentalPracticeDate',
        ),
      );
    }
  }

  private _initializeLegitimationFields = (
    countries: SelectBoxOption[],
    translations: (key: string, defaultValue?: string) => string,
    legitimationEntries: LegitimationsDetalj[],
  ) => {
    const entries = (
      legitimationEntries && legitimationEntries.length > 0
        ? legitimationEntries.slice(0, LEGITIMATION_MAX_ENTRIES)
        : [createEmptyLegitimationEntry()]
    ) as LegitimationsDetalj[];

    for (let index = 0; index < LEGITIMATION_MAX_ENTRIES; index++) {
      const entry = entries[index];
      const shouldShow = !!entry && index < entries.length;
      const countryFieldName = getLegitimationCountryFieldName(index);
      const dateFieldName = getLegitimationDateFieldName(index);

      const countryField = new LegitimationCountryField(
        countryFieldName,
        countries ?? [],
        entry?.LandId ?? '',
        translations,
      );
      countryField.ShowField = shouldShow;
      countryField.props.isRequired = shouldShow;
      this._map.set(countryFieldName, countryField);

      const dateField = new ExaminationDateField(
        dateFieldName,
        entry?.Datum ?? '',
        translations,
        'specialistbevisAnsokan/legitimationDateLabel',
      );
      dateField.ShowField = shouldShow;
      dateField.props.isRequired = shouldShow;
      this._map.set(dateFieldName, dateField);
    }
  };

  private _initializeEarlierSpecialistFields = (
    specialistOptions: SelectBoxOption[],
    countries: SelectBoxOption[],
    translations: (key: string, defaultValue?: string) => string,
    earlierSpecialistEntries: SpecialistbevisDetalj[],
  ) => {
    const entries = earlierSpecialistEntries?.slice(0, EARLIER_SPECIALIST_MAX_ENTRIES) ?? [];

    for (let index = 0; index < EARLIER_SPECIALIST_MAX_ENTRIES; index++) {
      const entry = entries[index];
      const shouldShow = !!entry && index < entries.length;

      const specialityFieldName = getEarlierSpecialistSpecialityFieldName(index);
      const specialityField = new EarlierSpecialistTextField(
        specialityFieldName,
        entry?.Specialitet ?? '',
        translations,
        'specialistbevisAnsokan/earlierSpecialistSpecialityLabel',
      );
      specialityField.ShowField = shouldShow;
      specialityField.props.isRequired = shouldShow;
      this._map.set(specialityFieldName, specialityField);

      const countryFieldName = getEarlierSpecialistCountryFieldName(index);
      const countryField = new EarlierSpecialistCountryField(
        countryFieldName,
        countries,
        entry?.LandId ?? '',
        translations,
      );
      countryField.ShowField = shouldShow;
      countryField.props.isRequired = shouldShow;
      this._map.set(countryFieldName, countryField);

      const dateFieldName = getEarlierSpecialistDateFieldName(index);
      const dateField = new EarlierSpecialistDateField(
        dateFieldName,
        entry?.Datum ?? '',
        translations,
        'specialistbevisAnsokan/earlierSpecialistDateLabel',
      );
      dateField.ShowField = shouldShow;
      dateField.props.isRequired = shouldShow;
      this._map.set(dateFieldName, dateField);
    }
  };

  toggleShowFileFor = (fieldName: SpecialistbevisAnsokanFormFieldName) => {
    const filesField = this._map.get(fieldName);
    if (!filesField) return;
    filesField.ShowField = !filesField.ShowField;
  };

  showFieldFor = (fieldName: SpecialistbevisAnsokanFormFieldName): boolean => {
    const field = this._map.get(fieldName);
    return !!field && field.ShowField;
  };

  addOnChangeEventHandler = (inputChanged, onChangeTermsAndCondition) => {
    this._map.forEach((base) => {
      base.props.onChange =
        base.props.fieldName === SpecialistbevisAnsokanFormFieldName.TermsAndConditions
          ? onChangeTermsAndCondition
          : inputChanged;
    });
  };

  get = (fieldName: SpecialistbevisAnsokanFormFieldName | string): BaseField => this._map.get(fieldName);

  clone = () => {
    this._map = new Map(this._map);
    return this;
  };

  removeAllNonExistingFields = () => {
    this._removeFilesWithNullValueId(SpecialistbevisAnsokanFormFieldName.FileUpload1);
    this._removeFilesWithNullValueId(SpecialistbevisAnsokanFormFieldName.FileUpload2);
    this._removeFilesWithNullValueId(SpecialistbevisAnsokanFormFieldName.FileUpload3);
    this._removeFilesWithNullValueId(SpecialistbevisAnsokanFormFieldName.FileUpload4);
    this._removeFilesWithNullValueId(SpecialistbevisAnsokanFormFieldName.FileUpload5);
    this._removeFilesWithNullValueId(SpecialistbevisAnsokanFormFieldName.FileUpload6);
    this._removeFilesWithNullValueId(SpecialistbevisAnsokanFormFieldName.FileUpload7);
    this._removeFilesWithNullValueId(SpecialistbevisAnsokanFormFieldName.FileUpload8);
    this._removeFilesWithNullValueId(SpecialistbevisAnsokanFormFieldName.FileUpload9);
    this._removeFilesWithNullValueId(SpecialistbevisAnsokanFormFieldName.FileUpload10);
    this._removeFilesWithNullValueId(SpecialistbevisAnsokanFormFieldName.FileUpload11);
    this._removeFilesWithNullValueId(SpecialistbevisAnsokanFormFieldName.FileUpload12);
  };

  private _removeFilesWithNullValueId = (fileFieldName: SpecialistbevisAnsokanFormFieldName) => {
    const field = this._map.get(fileFieldName);
    if (!field) return;
    const fileField = field.props;
    fileField.value = fileField.value.filter((file) => !!file.FileId);
  };

  /**
   * Does not actually deletes the file but just clears the state, sort of.
   */
  removeFilesByTag = (
    tag:
      | SpecialistbevisAnsokanFormFieldName.FileUpload1
      | SpecialistbevisAnsokanFormFieldName.FileUpload2
      | SpecialistbevisAnsokanFormFieldName.FileUpload3,
  ) => {
    const field = this._map.get(tag);
    if (!field) return;
    const fileField = field.props;
    fileField.value = [];
  };

  setFileValueByFieldName = (field: FormFieldFile) => {
    const fieldInMap = this._map.get(field.fieldName);
    if (!fieldInMap) return;
    const fileField = fieldInMap.props;
    fileField.value = field.value;
  };

  setErrorMessage = (message: string, tag: string) => {
    var fieldState = this._map.get(tag);
    if (message) {
      fieldState.props.errorMessage = message;
    }
  };

  setFileErrorMessage = (message: string, tag: string) => {
    var examinationFileState = this._map.get(tag);
    examinationFileState.props.errorMessage = message;
  };

  validateAllFields() {
    this.allFieldsAreValid = true;
    for (let key of Array.from(this._map.keys())) {
      const field = this._map.get(key);
      if (!field.ShowField) continue;
      const result = field.validate();
      if (!result.isValid) {
        this.allFieldsAreValid = false;
        return;
      }
    }
    const emailsAreSame = this.validateEmailComparison();
    this.allFieldsAreValid = emailsAreSame;
  }

  validateAllFieldsWithErrorMessages() {
    this.allFieldsAreValid = true;
    for (let key of Array.from(this._map.keys())) {
      const field = this._map.get(key);
      if (!field.ShowField) continue;
      const isValid = this.validateFieldWithErrorMessage(field);
      if (!isValid) {
        this.allFieldsAreValid = false;
      }
    }
  }

  validateFieldWithErrorMessage = (field: BaseField): boolean => {
    let fieldIsValid = field.validateWithErrorMessage();
    let emailsAreSame = field.shouldValidateUsingOtherFields ? this.validateEmailComparison() : true;

    const isValid = fieldIsValid && emailsAreSame;
    if (!isValid) this.allFieldsAreValid = false;
    return isValid;
  };

  validateEmailComparison = (): boolean => {
    const emailField = this._map.get(SpecialistbevisAnsokanFormFieldName.Email);
    const confirmEmailField = this._map.get(SpecialistbevisAnsokanFormFieldName.ConfirmEmail);

    const areDifferent = confirmEmailField.props.value && emailField.props.value !== confirmEmailField.props.value;
    confirmEmailField.props.errorMessage = areDifferent ? this._emailNoMatch : '';
    return !areDifferent;
  };
}

class BaseField {
  protected _props;
  public ShowField;
  protected _Translations: (key: string, defaultValue?: string) => string;
  constructor(
    fieldName: SpecialistbevisAnsokanFormFieldName | string,
    translations: (key: string, defaultValue?: string) => string,
    mandatory: boolean = true,
    showField: boolean = true,
  ) {
    this._props = {
      fieldName: fieldName,
      isRequired: mandatory,
      pattern: null,
      patternMessage: '',
      value: null, //NOTE: Value have different types in subtypes.
      errorMessage: '',
      infoMessage: '', //Everything should not be handled as an error but more like information
      actAsNumber: false,
      maxLength: null,
      showMaxLengthMessage: false,
      checkboxWithProps: false,
    };
    this._Translations = translations;
    this.ShowField = showField;
  }

  get props(): SpecialistbevisAnsokanField {
    return this._props;
  }

  validate = (): ValidationObject => {
    const props = this._props;
    const result = {
      isValid: true,

      errorMessage: '',
    };

    result.isValid = this.validateExists();
    if (!result.isValid) {
      result.errorMessage = this._Translations('errorMessages/required');
      return result;
    }

    result.isValid = this.validatePattern();
    if (!result.isValid) {
      result.errorMessage = props.patternMessage;
      return result;
    }
    return result;
  };

  validateWithErrorMessage = (): boolean => {
    const props = this._props;

    const result = this.validate();
    props.errorMessage = result.errorMessage;

    return result.isValid;
  };

  protected validateExists = (): boolean => {
    const props = this._props;
    const valueExists = !!props.value;
    return props.isRequired && valueExists;
  };

  protected validatePattern = (): boolean => {
    const props = this._props;
    const valueExists = !!props.value;
    if (!props.pattern || !valueExists) return true;
    return new RegExp(props.pattern).test(props.value);
  };

  get shouldValidateUsingOtherFields() {
    return false;
  }
}
class OvrigtSpecialistbevisTyp extends BaseField {
  constructor(
    fieldName: SpecialistbevisAnsokanFormFieldName,
    value: string,
    showField: boolean = true,
    translations: (key: string, defaultValue?: string) => string,
    required?: boolean,
  ) {
    super(fieldName, translations, showField);
    const props = this._props;
    props.label = `${translations('specialistbevisAnsokan/OtherSpecialistTypeLabel')} ${translations('mandatory')}`;
    props.value = value ?? '';
    props.isRequired = required;
  }

  validate = (): ValidationObject => {
    const result = {
      isValid: true,
      errorMessage: '',
    };

    const props = this._props;

    if (props.isRequired && !this.validateExists()) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/required');
      return result;
    }

    let maxLength = 255;
    if (props.value.length > maxLength) {
      result.isValid = false;
      result.errorMessage = this._Translations('toBigString');
      return result;
    }

    return result;
  };

  validateExists = () => {
    const props = this._props;
    return props.value.length > 0;
  };

  validateWithErrorMessage = (): boolean => {
    const props = this._props;

    const result = this.validate();
    props.errorMessage = result.errorMessage;

    return result.isValid;
  };
}
class EpostField extends BaseField {
  protected _show: boolean = true;
  private _shouldValidate: boolean;
  constructor(
    fieldName: SpecialistbevisAnsokanFormFieldName,
    value: string,
    shouldValidate: boolean = true,
    translations: (key: string, defaultValue?: string) => string,
  ) {
    super(fieldName, translations);
    const props = this._props;
    props.id = fieldName;
    props.autoComplete = 'email';
    props.label = `${translations('email')} ${translations('mandatory')}`;
    props.value = value ?? '';
    props.pattern =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zåäöA-ZÅÄÖ\-0-9]+\.)+[a-zåäöA-ZÅÄÖ]{2,}))$/;
    props.patternMessage = translations('errorMessages/emailWrongFormat');

    this._shouldValidate = shouldValidate;
  }

  get shouldValidateUsingOtherFields() {
    return true;
  }

  validate = (): ValidationObject => {
    const result = {
      isValid: true,
      errorMessage: '',
    };

    if (!this._shouldValidate) return result;

    const props = this._props;

    if (!this.validateExists()) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/required');
      return result;
    }

    let maxLength = 255;
    if (props.value.length > maxLength) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/toBigString');
      return result;
    }
    if (!this.validatePattern()) {
      result.isValid = false;
      result.errorMessage = props.patternMessage;
      return result;
    }

    const disallowedChars = ['å', 'Å', 'ä', 'Ä', 'ö', 'Ö'];
    for (let index = 0; index < disallowedChars.length; index++) {
      const char = disallowedChars[index];
      if (props.value.includes(char)) {
        result.isValid = false;
        result.errorMessage = this._Translations('errorMessages/disallowedChars');
        return result;
      }
    }

    return result;
  };

  validateExists = () => {
    const props = this._props;
    return props.isRequired && props.value.length > 0;
  };

  validateWithErrorMessage = (): boolean => {
    const props = this._props;

    const result = this.validate();
    props.errorMessage = result.errorMessage;

    return result.isValid;
  };
}

class CheckboxField extends BaseField {
  constructor(
    fieldName: SpecialistbevisAnsokanFormFieldName,
    translations: (key: string, defaultValue?: string) => string,
    value?: boolean,
  ) {
    super(fieldName, translations);
    const props = this._props;
    props.fieldName = SpecialistbevisAnsokanFormFieldName.CheckboxValue;
    props.isRequired = false;
    props.errorMessage = '';
    props.value = value;
  }

  validate = (): ValidationObject => ({
    isValid: true,
    errorMessage: '',
  });

  validateWithErrorMessage = (): boolean => {
    const props = this._props;
    const result = this.validate();

    props.errorMessage = result.errorMessage;
    return result.isValid;
  };
}
class UtbildningsLand extends BaseField {
  constructor(
    fieldName: SpecialistbevisAnsokanFormFieldName,
    countries: SelectBoxOption[],
    EuCountry: string,
    showField: boolean,
    translations: (key: string, defaultValue?: string) => string,
  ) {
    super(fieldName, translations, showField);
    const props = this._props;
    (props.label = `${translations('educationCountry')} ${translations('mandatory')}`),
      (props.defaultOptionText = translations('defaultOptionText'));
    props.className = 'm-b-2';
    props.options = countries.filter((x) => x.key !== '200043');
    props.value = EuCountry ?? '';
  }
}

class HasCertInOtherCountry extends BaseField {
  constructor(
    fieldName: SpecialistbevisAnsokanFormFieldName,
    translations: (key: string, defaultValue?: string) => string,
    value?: boolean,
    showField: boolean = true,
    ariaLabel?: string,
  ) {
    super(fieldName, translations, showField);
    const props = this._props;
    props.isRequired = false;
    props.label = `${translations('specialistbevisAnsokan/otherCountry')} ${translations('mandatory')}`;
    props.value = value;
    props.ariaLabel = ariaLabel;
  }
  validate = (): ValidationObject => {
    const result = {
      isValid: true,
      errorMessage: '',
    };

    const props = this._props;

    if (props.value !== true && this.props.value !== false) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/required');
      return result;
    }

    return result;
  };
}

//map the countries of type SelectBoxOption to items of type MultiSelectItem and set the isSelectedByDefault to true if the ID exists in the string array CurrentlySavedSavlues

class OtherCertCountry extends BaseField {
  constructor(
    fieldName: SpecialistbevisAnsokanFormFieldName,
    countries: SelectBoxOption[],
    value: string[],
    showField: boolean,
    translations: (key: string, defaultValue?: string) => string,
    isRequired?: boolean,
    addMultiSelectItem?: (selectedItems: MultiSelectItem[]) => void,
  ) {
    super(fieldName, translations, showField);
    const props = this._props;
    props.items = this.mapCountriesToMultiSelectItems(countries, value) as MultiSelectItem[];
    props.defaultOptionText = translations('defaultOtherCountryText');
    props.isRequired = isRequired;
    props.value = value ?? [];
    props.showAllOptionText = false;
    props.enableShowAllOption = false;
    props.textBoxLabelText = `${translations('ifHasOtherCountryText')} ${translations('mandatory')}`;
    props.showRemoveSelectionsButton = false;
    props.onSelectedItemsChange = addMultiSelectItem;
  }

  validate = (): ValidationObject => {
    const result = {
      isValid: true,
      errorMessage: '',
    };
    const props = this._props;
    if (props.isRequired) {
      if (props.isRequired) {
        if (props.value.length < 1) {
          result.isValid = false;
          result.errorMessage = this._Translations('errorMessages/requonlyFiveCountriesired');
          return result;
        }
      }
    }
    if (props.value.length > 5) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/onlyFiveCountries');
      return result;
    }

    return result;
  };

  mapCountriesToMultiSelectItems = (
    countries: SelectBoxOption[],
    CurrentlySavedValues: string[],
  ): MultiSelectItem[] => {
    return countries.map((country) => ({
      key: country.key,
      value: country.text,
      isSelectedByDefault: CurrentlySavedValues.includes(country.key),
    }));
  };
}

class SpecialistbevisTyper extends BaseField {
  constructor(
    fieldName: SpecialistbevisAnsokanFormFieldName,
    SpecialistbevisTyper: SelectBoxOption[],
    specialistBevisTypId: string,
    showField: boolean,
    translations: (key: string, defaultValue?: string) => string,
    isRequired: boolean = true,
  ) {
    super(fieldName, translations, isRequired, showField);

    const props = this._props;
    const labelSuffix = isRequired ? ` ${translations('mandatory')}` : '';
    props.label = `${translations('specialistbevisAnsokan/labelTypeOfSpec')}${labelSuffix}`;
    props.defaultOptionText = translations('specialistbevisAnsokan/defaultSpecialistOptionsText');
    props.placeholder = translations('specialistbevisAnsokan/defaultSpecialistOptionsText');
    props.isRequired = isRequired;
    props.items = SpecialistbevisTyper.map((x) => ({ key: x.key, value: x.text } as ComboboxItem));
    props.options = SpecialistbevisTyper;
    props.value = specialistBevisTypId ?? '';
  }
}
class ExaminationDateField extends BaseField {
  constructor(
    fieldName: SpecialistbevisAnsokanFormFieldName | string,
    examensDatum: string,
    translations: (key: string, defaultValue?: string) => string,
    labelKey: string = 'examinationDate',
    isRequired: boolean = true,
  ) {
    super(fieldName, translations, isRequired);
    const props = this._props;
    props.value = examensDatum ?? '';
    props.id = fieldName;
    props.isRequired = isRequired;
    props.label = isRequired ? `${translations(labelKey)} ${translations('mandatory')}` : translations(labelKey);
  }

  validate = (): ValidationObject => {
    const props = this._props;

    const result = {
      isValid: true,
      errorMessage: '',
    };

    const valueExists = !!props.value;

    if (props.isRequired && !valueExists) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/required');
      return result;
    }

    if (!valueExists) {
      return result;
    }

    //validate format
    const pattern = /^\d\d\d\d-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/;
    if (!pattern.test(props.value)) {
      result.errorMessage = this._Translations('errorMessages/correctDateFormat');
      result.isValid = false;
      return result;
    }

    //validate date
    const valueSplit = props.value.split('-');
    if (!isDateValid(valueSplit[0], valueSplit[1], valueSplit[2])) {
      result.errorMessage = this._Translations('errorMessages/correctDateError');
      result.isValid = false;
      return result;
    }

    //can't be higher than today
    if (Date.parse(props.value) > Date.now()) {
      result.errorMessage = this._Translations('errorMessages/FutureDateError');
      result.isValid = false;
      return result;
    }

    var enteredYear = parseInt(valueSplit[0], 10);
    if (enteredYear < 1900) {
      result.errorMessage = this._Translations('errorMessages/oldDateError');
      result.isValid = false;
      return result;
    }

    return result;
  };

  validateWithErrorMessage = (): boolean => {
    const props = this._props;
    const result = this.validate();

    props.errorMessage = result.errorMessage;
    return result.isValid;
  };
}

class GeneralDentalPracticeCountryField extends BaseField {
  constructor(
    fieldName: SpecialistbevisAnsokanFormFieldName,
    countries: SelectBoxOption[],
    value: string,
    translations: (key: string, defaultValue?: string) => string,
  ) {
    super(fieldName, translations);
    const props = this._props;
    props.label = `${translations('generalDentalPracticeCountry')} ${translations('mandatory')}`;
    props.defaultOptionText = translations('generalDentalPracticedefaultOptionText');
    props.options = (countries ?? []).filter((x) => x.key !== '200043');
    props.value = value ?? '';
  }
}

class LegitimationCountryField extends BaseField {
  constructor(
    fieldName: SpecialistbevisAnsokanFormFieldName | string,
    countries: SelectBoxOption[],
    value: string,
    translations: (key: string, defaultValue?: string) => string,
  ) {
    super(fieldName, translations);
    const props = this._props;
    props.id = fieldName;
    props.label = `${translations('specialistbevisAnsokan/legitimationCountryLabel')} ${translations('mandatory')}`;
    props.defaultOptionText = translations('specialistbevisAnsokan/legitimationCountryDefaultOptionText');
    props.options = countries ?? [];
    props.value = value ?? '';
  }
}

class EarlierSpecialistCountryField extends BaseField {
  constructor(
    fieldName: SpecialistbevisAnsokanFormFieldName | string,
    countries: SelectBoxOption[],
    value: string,
    translations: (key: string, defaultValue?: string) => string,
  ) {
    super(fieldName, translations);
    const props = this._props;
    props.id = fieldName;
    props.label = `${translations('specialistbevisAnsokan/earlierSpecialistCountryLabel')}`;
    props.defaultOptionText = translations('specialistbevisAnsokan/legitimationCountryDefaultOptionText');
    props.options = countries ?? [];
    props.value = value ?? '';
  }
}

class EarlierSpecialistSpecialityField extends BaseField {
  constructor(
    fieldName: SpecialistbevisAnsokanFormFieldName | string,
    specialistOptions: SelectBoxOption[],
    value: string,
    translations: (key: string, defaultValue?: string) => string,
  ) {
    super(fieldName, translations, false, false);
    const props = this._props;
    props.label = translations('specialistbevisAnsokan/earlierSpecialistSpecialityLabel');
    props.defaultOptionText = translations('specialistbevisAnsokan/defaultSpecialistOptionsText');
    props.placeholder = translations('specialistbevisAnsokan/defaultSpecialistOptionsText');
    props.options = specialistOptions ?? [];
    props.items = (specialistOptions ?? []).map((x) => ({ key: x.key, value: x.text } as ComboboxItem));
    props.value = value ?? '';
  }

  validate = (): ValidationObject => {
    const props = this._props;
    const result = {
      isValid: true,
      errorMessage: '',
    };

    if (props.isRequired && (!props.value || `${props.value}`.trim().length === 0)) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/required');
    }

    return result;
  };
}

class EarlierSpecialistTextField extends BaseField {
  constructor(
    fieldName: SpecialistbevisAnsokanFormFieldName | string,
    value: string,
    translations: (key: string, defaultValue?: string) => string,
    labelKey: string,
  ) {
    super(fieldName, translations, false, false);
    const props = this._props;
    props.label = translations(labelKey);
    props.value = value ?? '';
  }

  validate = (): ValidationObject => {
    const props = this._props;
    const result = {
      isValid: true,
      errorMessage: '',
    };

    const value = typeof props.value === 'string' ? props.value : '';
    const trimmedValue = value ? value.trim() : '';

    if (props.isRequired && trimmedValue.length === 0) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/required');
      return result;
    }

    if (trimmedValue.length > 255) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/toBigString');
      return result;
    }

    return result;
  };
}

class EarlierSpecialistDateField extends BaseField {
  constructor(
    fieldName: SpecialistbevisAnsokanFormFieldName | string,
    value: string,
    translations: (key: string, defaultValue?: string) => string,
    labelKey: string,
  ) {
    super(fieldName, translations, false, false);
    const props = this._props;
    props.label = translations(labelKey);
    props.value = value ?? '';
    props.id = fieldName;
  }

  validate = (): ValidationObject => {
    const props = this._props;
    const result = {
      isValid: true,
      errorMessage: '',
    };

    if (!props.value || (typeof props.value === 'string' && props.value.trim().length === 0)) {
      if (props.isRequired) {
        result.isValid = false;
        result.errorMessage = this._Translations('errorMessages/required');
      }
      return result;
    }

    const trimmedValue = (props.value as string).trim();
    const pattern = /^\d\d\d\d-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/;
    if (!pattern.test(trimmedValue)) {
      result.errorMessage = this._Translations('errorMessages/correctDateFormat');
      result.isValid = false;
      return result;
    }

    const valueSplit = trimmedValue.split('-');
    if (!isDateValid(valueSplit[0], valueSplit[1], valueSplit[2])) {
      result.errorMessage = this._Translations('errorMessages/correctDateError');
      result.isValid = false;
      return result;
    }

    if (Date.parse(trimmedValue) > Date.now()) {
      result.errorMessage = this._Translations('errorMessages/FutureDateError');
      result.isValid = false;
      return result;
    }

    const enteredYear = parseInt(valueSplit[0], 10);
    if (enteredYear < 1900) {
      result.errorMessage = this._Translations('errorMessages/oldDateError');
      result.isValid = false;
      return result;
    }

    return result;
  };
}
class FilesField extends BaseField {
  constructor(
    fieldName: SpecialistbevisAnsokanFormFieldName,
    label: string,
    value: FileExtendedDetails[],
    onAdd: (field: FormFieldFile) => void,
    onRemove: (file: FileExtendedDetails, fieldName: string, field: FormFieldFile) => void,
    translations: (key: string, defaultValue?: string) => string,
    mandatory: boolean = false,
    showField: boolean = true,
    isEducatedInSweden: boolean = false,
  ) {
    super(fieldName, translations, mandatory, showField);

    const hasAnyUploadedFromLeg = value.filter((x) => x.uploadedFromLegitimation).length > 0;
    const props = this._props;
    props.heading = label ?? '';
    props.max = 10;
    props.showMaxFilesInfoMessage = true;
    props.disabled = false;
    props.nofilesText = translations('noFileUploaded');
    props.onAdd = onAdd;
    props.onRemove = onRemove;
    props.errorMessage = null;
    props.value = value;
    props.mimetype = isEducatedInSweden ? SPEC_FILE_MIMETYPES : LEG_FILE_MIMETYPES;
    props.upload = translations('uploadFileText');
    props.maxFileSizeErrorMessage = translations('maxFileSizeErrorMessage');
    props.maxFilesInfoMessage = translations('maxFilesInfoMessage');
    props.illegalFileNameErrorMessage = translations('illegalFileNameErrorMessage');
    props.removeText = translations('removeText');
    props.removeFileText = translations('removeFileText');
    props.removeFilePopupBody = translations('removeFilePopupBody');
    props.abortText = translations('abortText');
    props.closeText = translations('closeText');
    props.checkMarkHeaderText = hasAnyUploadedFromLeg ? translations('uploadedFromLeg') : null;
  }

  validate = (): ValidationObject => {
    let result = {
      isValid: true,
      errorMessage: '',
    };

    if (this._props.isRequired && !this.validateExists()) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/required');
    }

    return result;
  };

  validateWithErrorMessage = (): boolean => {
    const props = this._props;
    const result = this.validate();

    props.errorMessage = result.errorMessage;

    return result.isValid;
  };

  validateExists = () => {
    const props = this._props;
    return props.value.length > 0;
  };
}
class TermsAndConditionsField extends BaseField {
  constructor(
    fieldName: SpecialistbevisAnsokanFormFieldName,
    label: string,
    translations: (key: string, defaultValue?: string) => string,
  ) {
    super(fieldName, translations);
    const props = this._props;
    props.label = label;
    props.value = false;
    //props.autoComplete = 'off';
  }

  validate = (): ValidationObject => ({
    isValid: this.props.value,
    errorMessage: '',
  });
  validateWithErrorMessage = (): boolean => {
    const result = this.validate();

    this.props.errorMessage = result.errorMessage;
    return result.isValid;
  };
}
class WorkPlace extends BaseField {
  constructor(
    fieldName: SpecialistbevisAnsokanFormFieldName,
    value: string,
    showField: boolean = true,
    translations: (key: string, defaultValue?: string) => string,
  ) {
    super(fieldName, translations, showField);
    const props = this._props;
    props.label = `${translations('workplace')} ${translations('mandatory')}`;
    props.value = value ?? '';
  }
}
